import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { switchMap } from 'rxjs/operators';

import { Transaction, TransactionVendorReference } from '@fry/payments/lib';
import { TracksOperationProgress } from '@fry/components/common';

import { ElementOptions } from '../integrations/stripe.integration';
import { PaymentGatewayIntegration } from '../integrations/integration';

/**
 * Stripe reports errors with more information and we should take
 * it into account. See: https://stripe.com/docs/api/errors
 */

/**
 * Component presenting Payment Gateway (Vendor) debit/credit card form
 *
 * This component is responsible for communicating with the Vendor in order
 * to tokenize the card details or execute any UI interaction with the Vendor.
 *
 * Outcome of the component interaction is the Card Token which can be
 * sent to backend to process card transaction (in case of Stripe).
 *
 * In case of Moneris this component returns when the Moneris checkout
 * interaction is finished. This can be successful payment, decline or cancel.
 */
@Component({
    selector: 'eas-payments-card-form',
    styleUrls: ['./transaction.card.form.component.css'],
    templateUrl: './transaction.card.form.component.html',
    standalone: false
})
export class TransactionCardFormComponent implements OnDestroy, AfterViewInit, TracksOperationProgress {
  /**
   * Reference to the element of the integration
   *
   * This is usually an entry point for the Vendor SDK to inject it's UI into the component.
   */
  @ViewChild('integrationContentElement') integrationContentElement!: ElementRef;

  /**
   * Integration used to process the payment
   */
  @Input() integration!: PaymentGatewayIntegration;

  /**
   * See the `TracksOperationProgress` interface
   */
  @Input() isOperationInProgress = false;

  /**
   * Transaction we are processing
   */
  @Input() transaction!: Transaction;

  @Output() cancel = new EventEmitter<void>();
  @Output() capture = new EventEmitter<TransactionVendorReference>();
  @Output() error = new EventEmitter<Error>();

  public form: UntypedFormGroup;

  /**
   * Can we change the payment method?
   *
   * We can only change payment method when there is more than one available
   * for our transaction.
   */
  public get canChangePaymentMethod(): boolean {
    return this.transaction.availableMethods.length > 1;
  }

  private elementOptions: ElementOptions = {
    classes: {
      base: 'eas-stripe__cardElement',
      complete: 'eas-stripe__cardElement--complete',
      empty: 'eas-stripe__cardElement--empty',
      focus: 'eas-stripe__cardElement--focus',
      invalid: 'eas-stripe__cardElement--invalid',
      webkitAutofill: 'eas-stripe__cardElement--webkit-autofill'
    }
  };

  constructor() {
    this.form = new UntypedFormGroup({
      token: new UntypedFormControl(''),
      name: new UntypedFormControl(''),
      informationCaptured: new UntypedFormControl('', Validators.required)
    });
  }

  ngAfterViewInit() {
    const changesHandler = (event: any) => {
      this.integration.handleUIElementChanges(event, this);
    };

    this.integration.initialize().pipe(
      switchMap(_configuration => {
        return this.integration.prepareVendorInteraction(this.transaction);
      })
    ).subscribe(
      (response) => {
        this.elementOptions = { ...this.elementOptions, ...response };
        this.integration.mountUIElement(this.integrationContentElement,
                                        this.elementOptions,
                                        changesHandler);
      },
      error => {
        this.error.emit(error);
      }
    );
  }

  ngOnDestroy() {
    this.integration.unmountUIElement();
  }

  onCancel($event) {
    $event.preventDefault();
    this.cancel.emit();
  }

  onCapture() {
    let options: Record<string, any> = {}
    if (this.integration.isMoneris) {
      options = {...this.elementOptions};
    }
    if (this.integration.isStripe) {
        if (this.form.value.name) {
          options.name = this.form.value.name;
        }
    }

    this.isOperationInProgress = true;
    this.integration.startVendorInteraction(this.transaction, options)
                    .subscribe(
                      (result) => {
                        this.isOperationInProgress = false;
                        if (this.integration.isStripe) { this.capture.emit(result); }
                      },
                      (error) => {
                        this.isOperationInProgress = false;
                        this.error.emit(error);
                      }
                    );
  }
}

/**
 * Mobile vallet support junkyard
 */

/*
class ... {

@ViewChild('payElement', { static: true }) payElement: ElementRef;

@Output() result = new EventEmitter<any>();

ngAfterViewInit() {
  ...

  const paymentRequestHandler = (event: any) => {
    const reference: TransactionVendorReference = this.integration.getVendorReference(event.paymentMethod);
    this.transactionStore.capture(this.transaction, reference).pipe(
      switchMap(progress => {
        return this.transactionStore.getWithDependencies(this.transaction.id)
                    .pipe(map(trans => [progress, trans]));
      }),
      tap(() => {
        event.complete('success');
      }),
      catchError(err => {
        console.log(err);
        event.complete('fail');
        throw err;
      })
    ).subscribe(result => {
      this.result.emit(result);
    });
  }

  this.integration.initialize().pipe(
    switchMap(_configuration => {
      return this.integration.prepareForVendorInteraction(this.transaction)
    })
  ).subscribe(
    (response) => {
      const paymentInfo = {
        amount: this.transaction.doc.amount * 100,
        currency: this.transaction.doc.currency.toLowerCase(),
        label: this.transaction.doc.subject?.name || this.transaction.doc.description,
      }
      this.integration.handlePaymentRequestButton(paymentInfo, this.payElement, paymentRequestHandler);
    },
    error => {
      this.error.emit(error);
    }
  );
}
*/
